'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useAppToast } from '@/hooks/useAppToast';
import { TourOverlay } from '@/components/help/TourOverlay';
import {
  findTourAnchor,
  getTour,
  measureTourTargetRect,
  type TourDef,
  type TourStepDef,
} from '@/lib/help/tours';
import {
  missingReasonForAnchor,
  prepareNavForTourAnchor,
  type TourMissingReason,
} from '@/lib/help/nav-tour-prepare';
import { apiClient } from '@/lib/api-client';

type TourContextValue = {
  activeTourId: string | null;
  stepIndex: number;
  startTour: (tourId: string) => void;
  next: () => void;
  prev: () => void;
  end: (opts?: { completed?: boolean }) => void;
};

const TourContext = createContext<TourContextValue | null>(null);

function trackTour(eventType: string, tourId: string, stepIndex?: number) {
  void apiClient
    .postHelpAnalyticsEvents([{ eventType, tourId, stepIndex }])
    .catch(() => undefined);
}

async function waitForAnchor(anchor: string, attempts = 20): Promise<HTMLElement | null> {
  for (let i = 0; i < attempts; i++) {
    const el = findTourAnchor(anchor);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto', inline: 'nearest' });
      // Let layout settle after scroll before measuring (avoids stale rect → tooltip under dim)
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      return el;
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  return null;
}

function readTargetRect(el: HTMLElement): DOMRect {
  return measureTourTargetRect(el);
}

function syncTourUrl(tourId: string, step: number) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('tour', tourId);
  url.searchParams.set('tourStep', String(step));
  window.history.replaceState({}, '', url.pathname + url.search);
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation('help');
  const { showError, showSuccess } = useAppToast();

  const [tour, setTour] = useState<TourDef | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [missingAnchor, setMissingAnchor] = useState(false);
  const [missingReason, setMissingReason] = useState<TourMissingReason>('generic');
  const [stepReady, setStepReady] = useState(false);
  const remeasureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRemeasure = useCallback((anchor: string) => {
    if (remeasureTimerRef.current) {
      clearTimeout(remeasureTimerRef.current);
    }
    // Sidebar margin animates ~300ms; remeasure so KPI grids don't keep a tall 2-col hole.
    remeasureTimerRef.current = setTimeout(() => {
      remeasureTimerRef.current = null;
      const live = findTourAnchor(anchor);
      if (live) setTargetRect(readTargetRect(live));
    }, 320);
  }, []);

  const clearTourUrl = useCallback(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('tour') || url.searchParams.has('tourStep')) {
      url.searchParams.delete('tour');
      url.searchParams.delete('tourStep');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  const end = useCallback(
    (opts?: { completed?: boolean }) => {
      if (tour && !opts?.completed) {
        trackTour('tour_abandon', tour.id, stepIndex);
      }
      setTour(null);
      setStepIndex(0);
      setTargetRect(null);
      setMissingAnchor(false);
      setMissingReason('generic');
      setStepReady(false);
      if (remeasureTimerRef.current) {
        clearTimeout(remeasureTimerRef.current);
        remeasureTimerRef.current = null;
      }
      clearTourUrl();
    },
    [clearTourUrl, stepIndex, tour]
  );

  const prepareStep = useCallback(
    async (def: TourDef, index: number) => {
      // Keep overlay up while resolving next step (avoids "frozen dim" with off-screen tooltip)
      const step = def.steps[index];
      if (!step) {
        end();
        return;
      }
      if (step.route && pathname !== step.route && !pathname.startsWith(`${step.route}/`)) {
        setStepReady(false);
        router.push(`${step.route}?tour=${encodeURIComponent(def.id)}&tourStep=${index}`);
        return;
      }
      if (step.anchor.startsWith('nav-')) {
        prepareNavForTourAnchor(step.anchor);
        await new Promise((r) => setTimeout(r, 360));
      }
      const el = await waitForAnchor(step.anchor);
      if (!el && step.anchor.startsWith('features-')) {
        const opener = findTourAnchor('tenants-manage-features');
        if (opener) {
          opener.click();
          await new Promise((r) => setTimeout(r, 350));
          const retry = await waitForAnchor(step.anchor, 30);
          if (!retry) {
            setMissingReason(missingReasonForAnchor(step.anchor));
            setMissingAnchor(true);
            setTargetRect(null);
            setStepReady(true);
            return;
          }
          setMissingAnchor(false);
          setMissingReason('generic');
          setTargetRect(readTargetRect(retry));
          setStepReady(true);
          scheduleRemeasure(step.anchor);
          return;
        }
      }
      if (!el) {
        setMissingReason(missingReasonForAnchor(step.anchor));
        setMissingAnchor(true);
        setTargetRect(null);
        setStepReady(true);
        return;
      }
      if (el.getAttribute('role') === 'tab') {
        el.click();
        await new Promise((r) => setTimeout(r, 120));
      }
      setMissingAnchor(false);
      setMissingReason('generic');
      setTargetRect(readTargetRect(el));
      setStepReady(true);
      scheduleRemeasure(step.anchor);
    },
    [end, pathname, router, scheduleRemeasure]
  );

  const startTour = useCallback(
    (tourId: string) => {
      const def = getTour(tourId);
      if (!def) {
        showError(t('tourNotFound'));
        return;
      }
      const role = user?.role || '';
      if (!def.roles.includes(role as 'User' | 'TenantAdmin' | 'SuperAdmin' | 'Supervisor')) {
        showError(t('tourWrongRole'));
        return;
      }
      setTour(def);
      setStepIndex(0);
      trackTour('tour_start', def.id, 0);
      const entry = def.entryRoute;
      if (pathname !== entry && !pathname.startsWith(`${entry}/`)) {
        router.push(`${entry}?tour=${encodeURIComponent(tourId)}&tourStep=0`);
        return;
      }
      syncTourUrl(tourId, 0);
      void prepareStep(def, 0);
    },
    [pathname, prepareStep, router, showError, t, user?.role]
  );

  // Resume after navigation via ?tour=&tourStep= (pathname only — not prepareStep,
  // otherwise in-page "Dalje" races with a stale tourStep=0 URL and freezes the overlay).
  // Do not track analytics here: startTour/next/end own those events. Tracking on
  // resume double-counts after router.push(...?tour=&tourStep=).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const tourId = params.get('tour');
    const stepRaw = params.get('tourStep');
    if (!tourId) return;
    const def = getTour(tourId);
    if (!def) return;
    const role = user?.role || '';
    if (!def.roles.includes(role as 'User' | 'TenantAdmin' | 'SuperAdmin' | 'Supervisor')) return;
    const idx = Math.max(0, Number(stepRaw || 0) || 0);
    setTour(def);
    setStepIndex(idx);
    void prepareStep(def, idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally pathname-driven
  }, [pathname, user?.role]);

  // Keep spotlight aligned on resize/scroll/layout (sidebar width, responsive grids)
  useEffect(() => {
    if (!tour) return;
    const step = tour.steps[stepIndex];
    if (!step) return;
    const update = () => {
      const el = findTourAnchor(step.anchor);
      if (el) setTargetRect(readTargetRect(el));
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const el = findTourAnchor(step.anchor);
    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
      if (el.parentElement) ro.observe(el.parentElement);
    }
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      ro?.disconnect();
    };
  }, [tour, stepIndex]);

  const next = useCallback(() => {
    if (!tour) return;
    const nextIdx = stepIndex + 1;
    if (nextIdx >= tour.steps.length) {
      trackTour('tour_complete', tour.id, stepIndex);
      showSuccess(t('tourCompleted'));
      end({ completed: true });
      return;
    }
    setStepIndex(nextIdx);
    trackTour('tour_step', tour.id, nextIdx);
    const step = tour.steps[nextIdx];
    if (step.route && pathname !== step.route && !pathname.startsWith(`${step.route}/`)) {
      router.push(`${step.route}?tour=${encodeURIComponent(tour.id)}&tourStep=${nextIdx}`);
      return;
    }
    syncTourUrl(tour.id, nextIdx);
    void prepareStep(tour, nextIdx);
  }, [tour, end, pathname, prepareStep, router, showSuccess, stepIndex, t]);

  const prev = useCallback(() => {
    if (!tour || stepIndex <= 0) return;
    const prevIdx = stepIndex - 1;
    setStepIndex(prevIdx);
    const step = tour.steps[prevIdx];
    if (step.route && pathname !== step.route && !pathname.startsWith(`${step.route}/`)) {
      router.push(`${step.route}?tour=${encodeURIComponent(tour.id)}&tourStep=${prevIdx}`);
      return;
    }
    syncTourUrl(tour.id, prevIdx);
    void prepareStep(tour, prevIdx);
  }, [tour, pathname, prepareStep, router, stepIndex]);

  const value = useMemo(
    () => ({
      activeTourId: tour?.id ?? null,
      stepIndex,
      startTour,
      next,
      prev,
      end,
    }),
    [tour?.id, stepIndex, startTour, next, prev, end]
  );

  const currentStep: TourStepDef | null = tour?.steps[stepIndex] ?? null;

  return (
    <TourContext.Provider value={value}>
      {children}
      {tour && currentStep && stepReady ? (
        <TourOverlay
          stepIndex={stepIndex}
          stepCount={tour.steps.length}
          title={t(currentStep.titleKey)}
          body={t(currentStep.bodyKey)}
          targetRect={targetRect}
          missingAnchor={missingAnchor}
          missingReason={missingReason}
          onNext={next}
          onPrev={prev}
          onClose={end}
        />
      ) : null}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTour must be used within TourProvider');
  }
  return ctx;
}

/** Safe hook when provider might be absent (e.g. tests). */
export function useTourOptional(): TourContextValue | null {
  return useContext(TourContext);
}
